import { useMemo } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';

import { openAxios } from 'services/axios';
import { OTO_LINK } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';

import { faCreditCardFront, faMoneyBill1 } from '@fortawesome/pro-light-svg-icons';

import { FormHeader, TAB_KEYS } from '../OTOLinkBuyer';
import { OrderTable } from './ContactDetailsTab';
import { SingleDeliveryCompany } from './SelectShippingMethodTab';
import * as S from './Tabs.styled';

const ConfirmOrderDetailsTab = ({ goToStep, onPaymentSuccess }) => {
	const { showError } = useAlert();
	const dispatch = useDispatch();
	const formMethods = useFormContext();
	const {
		watch,
		formState: { isSubmitting, isValid, isDirty }
	} = formMethods;

	const selectedDC = useMemo(
		() =>
			watch('deliveryCompanyList')?.find((dc) => dc.deliveryCompanySettingsId === watch('selectedDCSettingsId')),
		[watch('selectedDCSettingsId')]
	);

	const makeChargeCODWallet = async () => {
		dispatch(setLoading(true));
		try {
			const data = watch();
			const response = await openAxios.post(OTO_LINK.UPDATE_OTO_LINK_COD_PAYMENT, {
				key: data.key,
				GUID: data.GUID,
				otoLinkSessionId: data.otoLinkSessionId
			});
			if (response.status === 200) {
				onPaymentSuccess(response.data);
			} else {
				throw new Error(response);
			}
		} catch (error) {
			if (error?.response?.data?.code === 'error.L024') {
				// COD payment is successfully done, for backend
				onPaymentSuccess();
				return;
			}
			showError();
			AppLogger.error(error);
		} finally {
			dispatch(setLoading(false));
		}
	};

	const isPaymentMethodCC = useMemo(() => watch('paymentMethod') === 'CC', [watch('paymentMethod')]);

	const handleConfirm = () => {
		if (isPaymentMethodCC) {
			goToStep(TAB_KEYS.PAYMENT);
		} else {
			makeChargeCODWallet();
		}
	};

	return (
		<FormProvider {...formMethods}>
			<S.Card padding={0} background border={0} shadowed rounded>
				<S.Wrapper>
					<div>
						<FormHeader title="confirmOrderDetails" className="section-padding-bottom" />

						<SummarySection title="personalDetails">
							<OrderTable />
						</SummarySection>

						<SummarySection title="paymentMethod" goToStep={goToStep} step={TAB_KEYS.SELECT_PAYMENT_METHOD}>
							<S.PaymentTypeButton disabled className={isPaymentMethodCC && 'selected'}>
								<Icon
									icon={isPaymentMethodCC ? faCreditCardFront : faMoneyBill1}
									size="x-large"
									color="#183153"
								/>
								<Typography fontWeight="500">{isPaymentMethodCC ? 'creditCard' : 'cod'}</Typography>
							</S.PaymentTypeButton>
						</SummarySection>

						<SummarySection
							title="shippingMethod"
							goToStep={goToStep}
							step={TAB_KEYS.SELECT_SHIPPING_METHOD}>
							<SingleDeliveryCompany
								key={selectedDC?.deliveryCompanySettingsId}
								deliveryCompanySettingsId={selectedDC?.deliveryCompanySettingsId}
								deliveryTime={selectedDC?.deliveryTime}
								currency={selectedDC?.currency || watch('currency')}
								logo={selectedDC?.logo}
								name={selectedDC?.shippingContract?.name}
								serviceType={selectedDC?.serviceType}
								pickupType={selectedDC?.pickupType}
								price={selectedDC?.priceWithVat}
								disabled
							/>
						</SummarySection>

						<SummarySection title="shippingAddress" goToStep={goToStep} step={TAB_KEYS.CONTACT_DETAILS}>
							<S.ConfirmTabSummaryContainer>
								<Typography fontWeight="500">address</Typography>
								<Typography fontWeight="500" className="address-text">
									{watch('address.text')}
								</Typography>
							</S.ConfirmTabSummaryContainer>
						</SummarySection>

						<SummarySection title="totalAmount">
							<S.ConfirmTabSummaryContainer className="summary-column">
								<S.ConfirmTabSummaryRow>
									<Typography fontWeight="500">subTotal</Typography>
									<Typography withCurrency currency={watch('currency')}>
										{watch('subTotal') || 0}
									</Typography>
								</S.ConfirmTabSummaryRow>
								<S.ConfirmTabSummaryRow>
									<Typography fontWeight="500">shippingFee</Typography>
									<Typography withCurrency currency={watch('currency')}>
										{selectedDC?.priceWithVat || 0}
									</Typography>
								</S.ConfirmTabSummaryRow>
								<S.ConfirmTabSummaryRow>
									<Typography variant="p2" fontWeight="700">
										grandTotal
									</Typography>
									<Typography variant="p2" fontWeight="700" withCurrency currency={watch('currency')}>
										{watch('grandTotal') || 0}
									</Typography>
								</S.ConfirmTabSummaryRow>
							</S.ConfirmTabSummaryContainer>
						</SummarySection>
					</div>

					<S.FormRow id="confirm-form">
						<Button
							label="confirm"
							variant="blue"
							fullWidth
							onClick={handleConfirm}
							loading={isSubmitting}
							disabled={isValid ? false : !isDirty}
							size="xx-large"
						/>
					</S.FormRow>
				</S.Wrapper>
			</S.Card>
		</FormProvider>
	);
};

const SummarySection = ({ children, title, goToStep, step }) => (
	<S.ConfirmOrderContainer className="section-padding-bottom">
		<S.SubTitleAction>
			<Typography variant="p1" fontWeight="700">
				{title}
			</Typography>
			{step && (
				<Button
					variant="transparent"
					label="change"
					className="subtitle-action"
					onClick={() => goToStep(step)}
				/>
			)}
		</S.SubTitleAction>
		{children}
	</S.ConfirmOrderContainer>
);

export default ConfirmOrderDetailsTab;
